import React from 'react';
import { bespokeJourney, whatsappConfig } from '../utils/mockData';
import { useSection } from '../contexts/ContentContext';
import { MessageCircle, Scissors, Hammer, Package } from 'lucide-react';

const BespokeJourney = ({ onStartJourney, onOpenOrderTracking, onOpenDeliveryTracking }) => {
  const data = useSection('home.bespoke', {
    eyebrow: 'Your Journey',
    title: 'The Bespoke Experience',
    description: 'From first inquiry to final delivery, experience the art of personalized luxury',
    appointmentText: 'By Appointment Only',
    ctaText: 'Begin Your Journey',
    steps: bespokeJourney,
  });
  const steps = (data.steps && data.steps.length ? data.steps : bespokeJourney).map((s, i) => ({
    step: s.step ?? i + 1,
    title: s.title,
    description: s.description,
    icon: s.icon || 'message',
  }));

  const getIcon = (iconName) => {
    const iconMap = {
      message: MessageCircle,
      fabric: Scissors,
      craft: Hammer,
      delivery: Package,
    };
    const Icon = iconMap[iconName] || MessageCircle;
    return <Icon size={40} color="var(--accent-gold)" strokeWidth={1.5} />;
  };

  const handleStepClick = (step) => {
    switch (step.step) {
      case 1: {
        const message = encodeURIComponent(whatsappConfig.message);
        window.open(`https://wa.me/${whatsappConfig.number}?text=${message}`, '_blank');
        break;
      }
      case 2:
        if (onStartJourney) onStartJourney();
        break;
      case 3:
        if (onOpenOrderTracking) onOpenOrderTracking();
        break;
      case 4:
        if (onOpenDeliveryTracking) onOpenDeliveryTracking();
        break;
      default:
        break;
    }
  };

  return (
    <section id="bespoke" className="section-padding" style={styles.section}>
      <div className="luxury-container">
        <div style={styles.sectionHeader}>
          <p style={styles.sectionLabel}>{data.eyebrow}</p>
          <h2 className="heading-1">{data.title}</h2>
          <p className="body-large" style={styles.sectionDescription}>
            {data.description}
          </p>
        </div>

        <div style={styles.stepsContainer}>
          {steps.map((step, index) => (
            <React.Fragment key={step.step}>
              <div
                style={styles.stepCard}
                onClick={() => handleStepClick(step)}
                data-testid={`bespoke-step-${step.step}`}
              >
                <div style={styles.iconWrapper}>
                  {getIcon(step.icon)}
                </div>
                <h3 className="heading-3" style={styles.stepTitle}>{step.title}</h3>
                <p className="body-small" style={styles.stepDescription}>{step.description}</p>
                <span style={styles.stepCta}>
                  {step.step === 1 ? 'Click to start' :
                   step.step === 2 ? 'Click to inquire' :
                   step.step === 3 ? 'Click to track' : 'Click to track'}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div style={styles.connector}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={styles.ctaSection}>
          <p style={styles.appointmentText}>{data.appointmentText}</p>
          <button
            className="btn-primary"
            onClick={onStartJourney}
            data-testid="begin-journey-btn"
          >
            {data.ctaText}
          </button>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: {
    backgroundColor: 'var(--bg-primary)',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  sectionLabel: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '16px',
  },
  sectionDescription: {
    maxWidth: '600px',
    margin: '16px auto 0',
  },
  stepsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '60px',
  },
  stepCard: {
    width: '220px',
    padding: '32px 24px',
    background: 'var(--bg-secondary)',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  iconWrapper: {
    marginBottom: '20px',
  },
  stepTitle: {
    marginBottom: '12px',
  },
  stepDescription: {
    marginBottom: '16px',
    color: 'var(--text-secondary)',
    minHeight: '40px',
  },
  stepCta: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '10px',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
  },
  connector: {
    color: 'var(--border-light)',
    fontSize: '20px',
    alignSelf: 'center',
    padding: '0 8px',
  },
  ctaSection: {
    textAlign: 'center',
  },
  appointmentText: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    marginBottom: '24px',
  },
};

// Add hover styles
const hoverStyles = document.createElement('style');
hoverStyles.textContent = `
  #bespoke [data-testid^="bespoke-step"]:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
  @media (max-width: 1024px) {
    #bespoke > div > div:nth-child(2) > div:nth-child(even) {
      display: none !important;
    }
  }
  @media (max-width: 640px) {
    #bespoke > div > div:nth-child(2) > div:first-child,
    #bespoke > div > div:nth-child(2) > div:nth-child(3),
    #bespoke > div > div:nth-child(2) > div:nth-child(5),
    #bespoke > div > div:nth-child(2) > div:nth-child(7) {
      width: 100% !important;
    }
  }
`;
document.head.appendChild(hoverStyles);

export default BespokeJourney;
